import React from "react";
import type { FlippedProps, SerializableFlippedProps } from "../../types";

import { isFn, isObj } from "../../../../../utils/is-what";
import * as constants from "../../constants";

import { FlipContext } from "../../context";

const Flipped = (props: SerializableFlippedProps) => {
	const { children, flipId, inverseFlipId, ...rest } = props;
	let child = children;
	const isFunctionAsChildren = isFn(child);

	if (!isFunctionAsChildren) {
		try {
			child = React.Children.only(children);
		} catch (e) {
			throw new Error("Each Flipped component must wrap a single react element child!");
		}
	}

	// If nothing is being animated, assume everything is being animated.
	if (!rest.scale && !rest.translate && !rest.opacity) {
		Object.assign(rest, {
			translate: true,
			scale: true,
			opacity: true,
		});
	}

	const dataAttributes: Record<string, string | undefined> = {
		[constants.DATA_FLIP_CONFIG]: JSON.stringify(rest),
	};

	//TODO: We should improve the flow: query DOM elements -> Please use getElementById instead of querySelectorAll.
	if (flipId !== undefined) dataAttributes[constants.DATA_FLIP_ID] = String(flipId);
	else if (inverseFlipId) dataAttributes[constants.DATA_INVERSE_FLIP_ID] = String(inverseFlipId);

	if (isFunctionAsChildren) {
		return (child as Function)(dataAttributes);
	}
	return React.cloneElement(child as React.ReactElement<any>, dataAttributes);
};

export const FlippedWithContext = (props: FlippedProps) => {
	const {
		children,
		flipId,
		shouldFlip,
		shouldInvert,
		onAppear,
		onStart,
		onStartImmediate,
		onComplete,
		onExit,
		onSpringUpdate,
		...rest
	} = props;

	if (!children) return null;

	if (rest.inverseFlipId) {
		return <Flipped {...rest}>{children}</Flipped>;
	}

	return (
		<FlipContext.Consumer>
			{(flipCallbacks) => {
				if (isObj(flipCallbacks) && flipId) {
					flipCallbacks[flipId] = {
						shouldFlip,
						shouldInvert,
						onAppear,
						onStart,
						onStartImmediate,
						onComplete,
						onExit,
						onSpringUpdate,
					};
				}
				return (
					<Flipped flipId={flipId} {...rest}>
						{children}
					</Flipped>
				);
			}}
		</FlipContext.Consumer>
	);
};
FlippedWithContext.displayName = "Flipped";
